import { defineStore } from 'pinia';
import { io, type Socket } from 'socket.io-client';
import { computed, ref, watch, type Ref } from 'vue';

import { env } from '@/env';
import { USER } from '@/models/auth/permissions.enum';
import { useAuthStore } from '@/stores/auth';
import { useImpersonatorStore } from '@/stores/impersonator';
import { useTokenStore } from '@/stores/token';
import { logger } from '@/utils/logger';

const SOCKETIO_EVENT = {
  CONNECT: 'connect',
  RECONNECT: 'reconnect',
} as const;

export const EVENT = {
  REGISTER: 'register',
  REGISTER_USER: 'register_user',
  JOIN_BRAND_ROOMS: 'join_brand_rooms',
  NOTIFICATION: 'notification',
  PDF_GENERATED: 'pdf_generated',
  CSV_GENERATED: 'csv_generated',
  INTERACTIONS_CSV_GENERATED: 'INTERACTIONS_CSV_GENERATED',
  XLSX_GENERATED: 'xlsx_generated',
  SCHEDULED_POST_CALLBACK: 'scheduled_post_callback',
  SCHEDULED_POST_UPDATED: 'scheduled_post_updated',
  SCHEDULER_SETTINGS_UPDATED: 'scheduler_settings_updated',
  CLUSTER_TASK_NOTIFICATION: 'CLUSTER_TASK_NOTIFICATION',
  MEDIA_BULK_DOWNLOAD_GENERATED: 'media_bulk_download_generated',
  MEDIA_BULK_DOWNLOAD_FAILED: 'media_bulk_download_failed',
  MEDIA_MUTED: 'media_muted',
  MEDIA_MUTE_FAILED: 'media_mute_failed',
  MEDIA_MUTE_IN_PROGRESS: 'media_mute_in_progress',
  SONG_ID_COMPLETED: 'song_id_completed',
  PRODUCT_BULK_IMPORT_COMPLETED: 'product_bulk_import_completed',
  PRODUCT_BULK_IMPORT_FAILED: 'product_bulk_import_failed',
  COMPETITOR_RESOLUTION_UPDATED: 'tiktok_competitor_resolution_updated',
};

// socket.io's untyped event map takes `any[]` listeners; callers' payload types are their own contract.
type SocketListener = (...args: unknown[]) => void;
export type ProductBulkImportResult =
  | { status: 'completed'; successCount: number; errorCount: number }
  | { status: 'failed' };
type OnceWait = { onAbandoned?: () => void };
type DisconnectDetails = { message?: string; description?: unknown; context?: unknown };

export const useSocketStore = defineStore('socket', () => {
  const authStore = useAuthStore();
  const tokenStore = useTokenStore();
  const impersonatorStore = useImpersonatorStore();

  let socket: Socket | null = null;

  // Kept independently of the socket: `init()` replaces it, and socket.io listeners belong to the
  // instance they were attached to.
  const listeners = new Map<string, Set<SocketListener>>();

  // One-shot waits still pending. Unlike `listeners` these are NOT replayed onto a replacement
  // socket: it lands in different rooms, under a different identity when impersonating, so the
  // awaited event can never arrive and a replay would hang the caller instead of settling it.
  const pendingOnceWaits = new Set<OnceWait>();

  // The impersonation header the socket must be built with — stringified so a stored id that changes
  // representation (number vs the string sessionStorage hands back) isn't seen as a new identity.
  const onBehalfOf = computed(() =>
    impersonatorStore.isImpersonating ? String(impersonatorStore.impersonatedUserId) : null,
  );
  const id = ref<string>();
  let hasConnected = false;
  const reconnectedAt = ref<number | null>(null);

  // Payload hand-offs for two domains that read them through this store. Plain refs, so they cost
  // the transport no imports — the handlers that write them live in `app/socket-listeners.js`.
  // Rehoming them onto their own stores means touching an Options-API string watcher, so it is a
  // separate change.
  const productBulkImportResult = ref<ProductBulkImportResult | null>(null);
  const schedulerCallbackData = ref<Record<string, unknown> | null>(null);

  function registerIdentity() {
    const isLoggedIn = authStore.isLoggedIn;
    const hasToken = !!tokenStore.token;
    const notImpersonating = !impersonatorStore.isImpersonating;

    if (isLoggedIn && hasToken && socket?.connected) {
      socket.emit(EVENT.REGISTER, tokenStore.token);

      const canAccessCommunity = authStore.guard(USER.COMMUNITY.CAN_ACCESS_COMMUNITY);
      const canAccessScheduler = authStore.guard(USER.SCHEDULER.CAN_ACCESS_SCHEDULER);
      const canAccessCreators = authStore.guard(USER.CREATORS.CAN_ACCESS_CREATORS);
      if (notImpersonating && (canAccessScheduler || canAccessCommunity || canAccessCreators)) {
        socket.emit(EVENT.JOIN_BRAND_ROOMS, tokenStore.token);
      }
    }
  }

  function abandonPendingOnceWaits() {
    if (pendingOnceWaits.size === 0) return;

    const abandoned = [...pendingOnceWaits];
    pendingOnceWaits.clear();
    logger.warn('socket dropped while one-shot waits were pending', { count: abandoned.length });

    for (const { onAbandoned } of abandoned) {
      try {
        onAbandoned?.();
      } catch (error) {
        logger.error('socket one-shot wait failed to abandon cleanly', {}, error);
      }
    }
  }

  function disconnect() {
    if (socket) {
      socket.disconnect();
      socket = null;
    }
    abandonPendingOnceWaits();
  }

  function init() {
    if (socket) {
      disconnect();
    }

    const extraHeaders: Record<string, string> = {};
    if (onBehalfOf.value) {
      extraHeaders['X-On-Behalf-Of'] = onBehalfOf.value;
    }

    const created = io(env.notificationServiceUrl, { extraHeaders });
    socket = created;
    created.on(SOCKETIO_EVENT.CONNECT, () => {
      id.value = socket?.id;
      registerIdentity();
      // Events sent while disconnected are lost, so a reconnect tells listeners to refetch
      if (hasConnected) {
        reconnectedAt.value = Date.now();
      }
      hasConnected = true;
    });
    created.io.on(SOCKETIO_EVENT.RECONNECT, () => {
      id.value = socket?.id;
      registerIdentity();
    });
    created.on('disconnect', (reason, details?: DisconnectDetails) => {
      logger.debug(`socket io disconnected, reason: ${reason}`);
      if (details) {
        logger.debug(
          `socket io disconnected, details:
          message=${details.message}
          description=${String(details.description)}
          context=${String(details.context)}`,
        );
      }
      if (reason === 'io server disconnect') {
        logger.warn('socket io server disconnected. Trying to reconnect.');
        // the disconnection was initiated by the server, reconnect manually
        socket?.connect();
      }
    });
    replayListeners(created);
    warnOnUnhandledEvents(created);
  }

  // `onAny` sees no reserved events and is not counted by `listeners()`, so neither false-positives.
  function warnOnUnhandledEvents(target: Socket) {
    if (!env.isLocalDev) return;

    const warned = new Set<string>();
    target.onAny((event: string) => {
      if (target.listeners(event).length > 0 || warned.has(event)) return;
      warned.add(event);
      logger.warn(`socket event "${event}" arrived with no listener`, { event });
    });
  }

  function replayListeners(target: Socket) {
    for (const [event, callbacks] of listeners) {
      for (const callback of callbacks) {
        target.on(event, callback);
      }
    }
  }

  // Session-long listener. Safe before the socket exists and to repeat with the same callback;
  // callers never observe reconnects or socket replacement.
  // oxlint-disable-next-line typescript/no-unnecessary-type-parameters -- caller names its payload
  function addListener<T>(event: string, listener: (payload: T) => void) {
    const callback = listener as SocketListener;
    const callbacks = listeners.get(event) ?? new Set();
    if (callbacks.has(callback)) return;

    callbacks.add(callback);
    listeners.set(event, callbacks);
    if (socket) {
      socket.on(event, callback);
    }
  }

  // oxlint-disable-next-line typescript/no-unnecessary-type-parameters -- caller names its payload
  function removeListener<T>(event: string, listener: (payload: T) => void) {
    const callback = listener as SocketListener;
    listeners.get(event)?.delete(callback);
    if (socket) {
      socket.off(event, callback);
    }
  }

  // One-shot listener; `filter` keeps it armed past non-matching payloads. `onAbandoned` runs if the
  // socket is torn down or replaced first — the event can't reach a replacement, so callers settle.
  function addOnceListener<T>(
    event: string,
    callback: (payload: T) => void,
    filter?: (payload: T) => boolean,
    onAbandoned?: () => void,
  ) {
    if (!socket) return () => {};

    const wait: OnceWait = { onAbandoned };
    let handler: (payload: T) => void;
    if (filter) {
      handler = (payload) => {
        if (!filter(payload)) return;
        pendingOnceWaits.delete(wait);
        socket?.off(event, handler);
        callback(payload);
      };
      socket.on(event, handler);
    } else {
      handler = (payload) => {
        pendingOnceWaits.delete(wait);
        callback(payload);
      };
      socket.once(event, handler);
    }
    pendingOnceWaits.add(wait);

    return () => {
      pendingOnceWaits.delete(wait);
      socket?.off(event, handler);
    };
  }

  // Impersonating keeps isLoggedIn true, yet X-On-Behalf-Of is fixed at socket construction.
  watch(
    [() => authStore.isLoggedIn, onBehalfOf],
    ([isLoggedIn]) => {
      if (!isLoggedIn) {
        disconnect();
        return;
      }
      // Shared (share-token) sessions satisfy isLoggedIn but get no live
      // updates by design — recipients refresh to see changes.
      if (tokenStore.isShareToken) {
        return;
      }
      init();
    },
    { immediate: true },
  );

  return {
    // Pre-existing gap: unset until first connect; callers already send it as-is.
    id: id as Ref<string>,
    init,
    addListener,
    addOnceListener,
    removeListener,
    registerIdentity,
    reconnectedAt,
    productBulkImportResult,
    schedulerCallbackData,
  };
});
